// useGooglePlaces.js
import { useCallback, useRef } from "react";

const GOOGLE_LIBS = "places,geometry";

// shared across the whole app runtime
let googleMapsLoaderPromise = null;

function loadGoogleMapsApi(key) {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Maps requires window"));
  }

  const isReady = () => window.google?.maps?.places;

  if (isReady()) return Promise.resolve();
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const existing = Array.from(document.scripts).find((s) =>
      s.src?.includes("maps.googleapis.com/maps/api/js")
    );

    const onLoad = () =>
      isReady() ? resolve() : reject(new Error("Places not available"));
    const onError = (e) => reject(e);

    if (existing) {
      existing.addEventListener("load", onLoad, { once: true });
      existing.addEventListener("error", onError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      key || ""
    )}&libraries=${GOOGLE_LIBS}&language=en`;
    script.onload = onLoad;
    script.onerror = onError;
    document.head.appendChild(script);
  });

  return googleMapsLoaderPromise;
}

export function useGooglePlaces({ apiKey }) {
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);

  const ensureReady = useCallback(async () => {
    if (!apiKey) throw new Error("Missing Google API key");
    await loadGoogleMapsApi(apiKey);

    autocompleteServiceRef.current ??=
      new window.google.maps.places.AutocompleteService();

    placesServiceRef.current ??= new window.google.maps.places.PlacesService(
      document.createElement("div")
    );
  }, [apiKey]);

  const getPredictions = useCallback(
    async ({ input, country = "us", limit = 5 }) => {
      await ensureReady();

      return new Promise((resolve) => {
        autocompleteServiceRef.current.getPlacePredictions(
          {
            input,
            componentRestrictions: { country },
            types: ["address"],
          },
          (predictions, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              Array.isArray(predictions)
            ) {
              resolve(predictions.slice(0, limit));
            } else {
              resolve([]);
            }
          }
        );
      });
    },
    [ensureReady]
  );

  const getPlaceDetails = useCallback(
    async ({
      placeId,
      fields = ["address_components", "formatted_address", "geometry"],
    }) => {
      await ensureReady();

      return new Promise((resolve, reject) => {
        const svc = placesServiceRef.current;
        if (!svc?.getDetails)
          return reject(new Error("PlacesService not ready"));

        svc.getDetails({ placeId, fields }, (place, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
            return reject(new Error(`PlacesService status: ${status}`));
          }
          if (!place) return reject(new Error("No place returned"));
          resolve(place);
        });
      });
    },
    [ensureReady]
  );

  return { ensureReady, getPredictions, getPlaceDetails };
}
